import { useState, useEffect, useCallback } from 'react';

export const useReminderSystem = (reminderService, conversationManager) => {
  const [activeReminders, setActiveReminders] = useState([]);
  const [showReminderSetModal, setShowReminderSetModal] = useState(false);
  const [showReminderTriggeredModal, setShowReminderTriggeredModal] = useState(false);
  const [currentReminder, setCurrentReminder] = useState(null);
  const [reminderRequested, setReminderRequested] = useState(false);

  // Check for reminder requests in user speech
  const checkForReminderRequest = useCallback((userText) => {
    if (!userText || !reminderService || typeof reminderService.setReminder !== 'function') {
      console.warn('Cannot process reminder request - reminderService not available');
      return false;
    }
    
    // Clean and log the input for debugging
    const cleanText = userText.toLowerCase().trim();
    console.log('🎤 Checking voice input for reminders:', cleanText);
    
    // More flexible reminder patterns for natural speech
    const reminderPatterns = [
      /remind me (?:to )?(.+?) (?:in|at) (.+)/i,
      /set (?:a )?reminder (?:to )?(.+?) (?:for|in|at) (.+)/i,
      /(?:can you )?remind me (?:to )?(.+?) (?:in|at) (.+)/i,
      /I need (?:a )?reminder (?:to )?(.+?) (?:in|at) (.+)/i,
      /reminder (?:to )?(.+?) (?:in|at) (.+)/i
    ];
    
    for (const pattern of reminderPatterns) {
      const match = userText.match(pattern);
      if (match) {
        const message = match[1].trim();
        const timeStr = match[2].trim();
        
        try {
          const reminder = reminderService.setReminder(timeStr, message);
          if (reminder) {
            console.log('🎤 Voice reminder created:', reminder);
            
            // Update state
            setCurrentReminder(reminder);
            setShowReminderSetModal(true);
            setReminderRequested(true);
            
            // Force update the activeReminders state
            if (typeof reminderService.getActiveReminders === 'function') {
              const updatedReminders = reminderService.getActiveReminders();
              console.log('📊 Updated active reminders after voice:', updatedReminders);
              setActiveReminders(updatedReminders);
            }
            
            // Add confirmation to conversation
            conversationManager.addMessageToConversation({
              type: 'system',
              content: `⏰ Voice reminder set: "${message}" for ${reminder.scheduledTime.toLocaleString()}`,
              timestamp: new Date()
            });
            
            console.log('✅ Voice reminder set successfully, activeReminders updated');
            return true;
          }
        } catch (error) {
          console.error('Failed to set voice reminder:', error);
          conversationManager.addMessageToConversation({
            type: 'system',
            content: `❌ Sorry, I couldn't set that reminder. Please try a different time format.`,
            timestamp: new Date()
          });
        }
        break;
      }
    }
    
    return false;
  }, [reminderService, conversationManager]);

  // Handle reminder snoozing
  const handleReminderSnooze = useCallback((minutes) => {
    if (!currentReminder) return;
    
    try {
      reminderService.setReminder(`in ${minutes} minutes`, currentReminder.message);
      console.log(`Reminder snoozed for ${minutes} minutes`);
      
      conversationManager.addMessageToConversation({
        type: 'system',
        content: `⏰ Reminder snoozed for ${minutes} minutes`,
        timestamp: new Date()
      });
      
    } catch (error) {
      console.error('Failed to snooze reminder:', error);
    }
  }, [currentReminder, reminderService, conversationManager]);

  // Handle reminder cancellation
  const handleReminderCancel = useCallback((reminderId) => {
    const success = reminderService.cancelReminder(reminderId);
    if (success) {
      conversationManager.addMessageToConversation({
        type: 'system',
        content: '🗑️ Reminder cancelled',
        timestamp: new Date()
      });
      
      // Update active reminders
      if (typeof reminderService.getActiveReminders === 'function') {
        setActiveReminders(reminderService.getActiveReminders());
      }
    }
  }, [reminderService, conversationManager]);

  // Close reminder set modal
  const handleCloseReminderSetModal = useCallback(() => {
    setShowReminderSetModal(false);
    setCurrentReminder(null);
  }, []);

  // Close reminder triggered modal
  const handleCloseReminderTriggeredModal = useCallback(() => {
    setShowReminderTriggeredModal(false);
    setCurrentReminder(null);
  }, []);

  // Handle reminder triggered
  const handleReminderTriggered = useCallback((reminder) => {
    console.log('Reminder triggered:', reminder);
    
    setCurrentReminder(reminder);
    setShowReminderTriggeredModal(true);
    
    // Add to conversation if session is active
    if (conversationManager.sessionActive) {
      conversationManager.addMessageToConversation({
        type: 'system',
        content: `🔔 Reminder: ${reminder.message}`,
        timestamp: new Date()
      });
    }
  }, [conversationManager]);

  // Set up reminder monitoring
  useEffect(() => {
    // Only proceed if reminderService exists and has the required methods
    if (!reminderService || typeof reminderService.checkTriggeredReminders !== 'function') {
      console.warn('ReminderService not properly initialized or missing checkTriggeredReminders method');
      return;
    }
    
    // Check for triggered reminders every 30 seconds
    const reminderCheckInterval = setInterval(() => {
      try {
        if (reminderService && typeof reminderService.checkTriggeredReminders === 'function') {
          const triggered = reminderService.checkTriggeredReminders();
          if (Array.isArray(triggered)) {
            triggered.forEach(handleReminderTriggered);
          }
          
          // Update active reminders list
          if (typeof reminderService.getActiveReminders === 'function') {
            setActiveReminders(reminderService.getActiveReminders());
          }
        }
      } catch (error) {
        console.error('Error checking reminders:', error);
      }
    }, 30000);
    
    // Initial check
    try {
      if (typeof reminderService.getActiveReminders === 'function') {
        setActiveReminders(reminderService.getActiveReminders());
      }
    } catch (error) {
      console.error('Error getting initial active reminders:', error);
    }
    
    return () => {
      clearInterval(reminderCheckInterval);
    };
  }, [reminderService, handleReminderTriggered]);

  return {
    // States
    activeReminders,
    showReminderSetModal,
    showReminderTriggeredModal,
    currentReminder,
    reminderRequested,
    
    // Actions
    checkForReminderRequest,
    handleReminderSnooze,
    handleReminderCancel,
    handleCloseReminderSetModal,
    handleCloseReminderTriggeredModal,
    
    // Setters
    setReminderRequested,
    setActiveReminders,
    setCurrentReminder,
    setShowReminderSetModal,
    setShowReminderTriggeredModal
  };
};